package com.devi3east.bashbuddy.dashboard.ui

import androidx.compose.foundation.Image
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.devi3east.bashbuddy.common.util.FullScreenResViewer
import com.devi3east.bashbuddy.features.app.data.AppState
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.painterResource

@Composable
fun InformationScreen(
    appState: AppState,
    images: List<DrawableResource>,
    onBack: () -> Unit
) {
    var selectedImageIndex by remember { mutableStateOf(-1) }

    BoxWithConstraints(modifier = Modifier.fillMaxSize()) {
        val maxWidthDp = maxWidth
        val targetWidth = when {
            maxWidthDp < 600.dp -> maxWidthDp
            maxWidthDp < 900.dp -> 600.dp
            else -> 720.dp
        }

        val contentModifier = Modifier
            .widthIn(max = targetWidth)
            .align(Alignment.TopCenter)

        Column(
            modifier = contentModifier
                .padding(horizontal = 16.dp)
                .verticalScroll(rememberScrollState()),
            verticalArrangement = Arrangement.spacedBy(20.dp)
        ) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                modifier = Modifier.fillMaxWidth()
            ) {
                IconButton(onClick = onBack) {
                    Icon(
                        imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                        contentDescription = "Back",
                        tint = MaterialTheme.colorScheme.onSurface
                    )
                }

                Spacer(Modifier.width(8.dp))

                Text(
                    text = "Ticket Creation Guide",
                    style = MaterialTheme.typography.titleLarge.copy(
                        fontWeight = FontWeight.Bold
                    ),
                    color = MaterialTheme.colorScheme.onSurface
                )
            }

            // Intro
            Text(
                text = "This guide explains each field in the ticket creation form. Use it to ensure tickets are set up correctly for your fundraiser or event.",
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                textAlign = TextAlign.Center
            )

            // --- Ticket Type Overview ---
            InfoSectionHeader("Ticket Types")

            InfoRow(
                title = "Digital Tickets",
                description = "Digital tickets exist entirely online within bash buddy™ — no physical copies are printed, distributed, or mailed. \n" +
                        "When a user purchases a digital ticket, the system automatically generates a unique serial number and assigns the correct number of chance numbers based on the organization’s setup (e.g., “4 chances per ticket”). \n" +
                        "\n" +
                        "Each ticket and its assigned numbers are securely stored and viewable in the purchaser’s dashboard. \n" +
                        "Organizations can view every purchase in real time, including ticket numbers, buyer contact information, and entry details — all directly from their organization dashboard.\n" +
                        "\n" +
                        "Digital tickets are ideal for all raffle types. \n" +
                        "They eliminate the need for manual input or tracking and reduce administrative work while providing an instant, paperless experience for both buyers and organizers.\n",
                example = "Example: All ticket sales are conducted online through bash buddy™. The organization sets the number of tickets to be sold and all ticket details. Ticket assignment and tracking are fully automated by the platform. Organizations can monitor sales and view ticket assignments in real time through their dashboard. Once the tickets have sold out, the organization conducts the drawing and notifies winners accordingly."
            )

            InfoRow(
                title = "Printed Tickets",
                description = "Printed tickets represent a physical ticket, pull tab, gusher, or tip board. Choose this type if you already have physical tickets but want to offer online availability as well. It’s important to provide complete and accurate details when adding printed tickets. After a user purchases a printed ticket through bash buddy™, the organization must manually assign ticket numbers through their dashboard. Organizations can also choose whether to mail physical copies. A badge icon appears next to ticket titles when number assignments are pending.\n",
                example = "Example: An organization prints 2,500 physical event tickets with two chances to win per ticket and lists 500 of them for online purchase through bash buddy™ while selling the remaining 2,000 locally. These would be listed with a 'Quantity' of 500, 'Max Quantity' of 2,500, and 'Chances per Ticket' of 2. Online stock can be adjusted later as needed — we just need the full picture to maintain accurate analytics and assist with number assignments."
            )

            Spacer(Modifier.height(16.dp))

            // --- Shared Fields ---
            InfoSectionHeader("Common Fields (All Ticket Types)")

            InfoRow(
                title = "Title",
                description = "The official name of the ticket or raffle. Keep it brief but descriptive so buyers can quickly recognize it.",
                example = "Example: '2026 Honda SXS Raffle Ticket'"
            )

            InfoRow(
                title = "Ticket Cost",
                description = "The price for one ticket. Required for accurate revenue tracking, analytics, and online purchase.",
                example = "Example: $10.00"
            )

            InfoRow(
                title = "Chances per Ticket",
                description = "Indicates how many unique numbers are assigned per purchased ticket. Some tickets have 1, others may have 2, 4, 5, or up to 10. This determines the total range of draw numbers generated at purchase time.",
                example = "Example: A single event ticket may have 'two chances to win', meaning 2 unique numbers are assigned per ticket purchased. Common pull tabs often have 5 chances per ticket."
            )

            InfoRow(
                title = "Age Restriction",
                description = "Specifies the minimum age required to purchase. Use 0 if unrestricted. Include explanations for prizes with age-limited eligibility, such as alcohol or firearms.",
                example = "Example: 18 (must be 18+ to purchase)"
            )

            InfoRow(
                title = "50/50 Ticket",
                description = "Enable only when offering a 50/50-style jackpot. This flag allows the platform to dynamically display the total jackpot amount on event pages as sales occur.",
                example = "Example: As the jackpot increases with each sale, the event page will show the current total automatically. Larger jackpots can boost user interest and sales!"
            )

            InfoRow(
                title = "Description",
                description = "A concise explanation of what the ticket represents. Include relevant prize details, restrictions, or drawing rules as needed.",
                example = "Example: 'Enter to win a 2026 Honda SXS. Only 200 tickets available. Each ticket has 5 chances to win!'"
            )

            InfoRow(
                title = "Image",
                description = "Add a visual reference, such as a logo or prize photo. This image appears on the event page and in the user's dashboard.",
                example = "Example: Upload a clear photo of the raffle prize or promotional artwork."
            )

            Spacer(Modifier.height(16.dp))

            // --- Digital Tickets ---
            InfoSectionHeader("Digital Ticket Fields")

            InfoRow(
                title = "Max Quantity",
                description = "The maximum number of digital tickets to be created for the event or raffle. This value cannot be edited once set. Used to calculate the ticket number range when assigning unique numbers to each digital ticket purchase.",
                example = "Example: Max quantity of 200 digital tickets with 2 'chances per ticket' results in 200 tickets to be sold online. A single ticket would include two unique random numbers between 000-399."
            )

            Spacer(Modifier.height(16.dp))

            // --- Printed Tickets ---
            InfoSectionHeader("Printed Ticket Fields")

            InfoRow(
                title = "Serial Number",
                description = "Used to identify and track batches of pre-printed or physical tickets. Each printed bundle, tip board, or pull tab typically has a unique serial number. This is automatically generated for digital tickets and used behind the scenes.",
                example = "Example: 'PT-2026-001'"
            )

            InfoRow(
                title = "Shipping Fee",
                description = "Optional flat-rate fee to cover mailing or handling of physical tickets. This fee is applied once per order.",
                example = "Example: When a $1.50 shipping fee is added to a $5.00 printed ticket, purchasing three of those tickets would have a subtotal of $16.50 ($15.00 for tickets + $1.50 shipping). When a user purchases multiple tickets from a single event, the shipping charge is only charged once per order."
            )

            InfoRow(
                title = "Quantity",
                description = "The number of printed tickets available for online sale through bash buddy™. This value *can* be adjusted later through the dashboard — for instance, to move additional tickets online or reduce online availability when selling locally.",
                example = "Example: 500 (of 2,500 max quantity) printed event tickets or 125 (of 250 max quantity) pull tab tickets made available online."
            )

            InfoRow(
                title = "Max Quantity",
                description = "The total number of tickets for the event, in the bundle, or on the tip board. This includes both locally sold and online tickets. This value cannot be changed after creation. Used to maintain accurate total stock records.",
                example = "Example: 2,500 tickets sold for the event or a pull tab bundle containing 250 tickets, even if a lesser number is made available online."
            )

            Spacer(Modifier.height(8.dp))

            InfoSectionHeader("Digital Ticket Examples")

            InfoRow(
                title = "Example 1: 4 Number Pull Tab (Digital)",
                description = "To create a digital version of the 4-number pull tab where no printed tickets are needed:",
                example = """
1. Tap Add Ticket.
2. Select DIGITAL.
3. Enter:
   • Title: LOTTO 4 JACKPOT – DIGITAL DRAW
   • Ticket Cost: $10.00
4. Enter 250 for Max Quantity (total number of tickets available).
5. Set Chances per Ticket to 4 (each digital ticket will automatically receive four unique 3-digit numbers, 000-999).
6. Set Age Restriction if applicable.
7. Leave 50/50 Ticket unchecked.
8. Add details in Description.
9. Upload an image or promotional banner for the draw.

💡 These digital tickets do not require printed copies or manual number entry. Each ticket sold is automatically assigned four unique chance numbers. All chances are tracked securely in the system and can be viewed by the organization at any time.
    """.trimIndent()
            )

            InfoRow(
                title = "Example 2: Tip Board (Digital)",
                description = "To create a digital pull-tab style board where each tab is sold online only:",
                example = """
1. Tap Add Ticket.
2. Select DIGITAL.
3. Enter:
   • Title: 10 POUNDS BACON
   • Ticket Cost: $2.00
4. Enter 50 for Max Quantity (total number of digital tabs).
5. Set Chances per Ticket to 1 (each ticket automatically receives one unique 2-digit number, 00-49).
6. Set Age Restriction if applicable.
7. Leave 50/50 Ticket unchecked.
8. Add details in Description.
9. Upload an image or promotional banner for the draw.

💡 These digital tickets do not require printed copies or manual number entry. Each ticket sold is automatically assigned a unique chance number. All chances are tracked securely in the system and can be viewed by the organization at any time.
    """.trimIndent()
            )

            Spacer(Modifier.height(8.dp))

            InfoSectionHeader("Printed Ticket Examples")

            InfoRow(
                title = "Example 1: LOTTO 4 JACKPOT Ticket",
                description = "To list a LOTTO 4 JACKPOT ticket (like the example shown below) for online AND local sales:",
                example = """
1. Tap Add Ticket.
2. Select PRINTED.
3. Enter:
   • Title: LOTTO 4 JACKPOT – GUN TABLE #1
   • Serial Number: 11940
   • Ticket Cost: $10.00
   • Shipping Fee: $0.00 (unless applicable)
4. Enter 125 for Quantity (e.g., 125 for half of a 250-ticket bundle).
5. Set Max Quantity to the total number of tickets in the bundle (250).
6. Set Chances per Ticket to 4 (each ticket has FOUR 3-digit numbers).
7. Set Age Restriction if applicable.
8. Leave 50/50 Ticket unchecked.
9. Add details in Description.
10. Upload an image of the ticket, advertisement, or prize.

💡 This setup allows half of your LOTTO 4 JACKPOT tickets to be sold online while reserving the other half for local, in-person sales. Online stock can be adjusted later as needed. As online tickets are sold, a notification badge will display on the ticket icon. The organization will then have to open the ticket and enter the numbers manually for each online purchase.
    """.trimIndent()
            )

            InfoRow(
                title = "Example 2: SURE TIP -50- Board Ticket",
                description = "To list a pull tab ticket (like the example shown below) for online sales ONLY:",
                example = """
For a SURE TIP board where all tickets will be sold online:

1. Tap Add Ticket.
2. Select PRINTED.
3. Enter:
   • Title: SURE TIP – 10 POUNDS BACON
   • Serial Number: 20040
   • Ticket Cost: $2.00
   • Shipping Fee: $0.00 (unless applicable)
4. Enter 50 for Quantity and Max Quantity (all online).
5. Set Chances per Ticket to 1 (each tab has ONE 2-digit number).
6. Set Age Restriction if applicable.
7. Leave 50/50 Ticket unchecked.
8. Add details in Description.
9. Upload an image of the ticket, advertisement, or prize.

💡 This setup means all 50 tickets from this tip board will be sold exclusively online—no local tickets remain. As online tickets are sold, a notification badge will display on the ticket icon. The organization will then have to open the ticket and enter the number manually for each online purchase.
    """.trimIndent()
            )

            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .horizontalScroll(rememberScrollState()),
                horizontalArrangement = Arrangement.spacedBy(8.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                images.forEachIndexed { index, drawableRes ->
                    Image(
                        painter = painterResource(drawableRes),
                        contentDescription = "Printed Ticket Example",
                        modifier = Modifier
                            .size(140.dp)
                            .clickable { selectedImageIndex = index },
                        contentScale = ContentScale.Crop
                    )
                }
            }

            Spacer(Modifier.height(24.dp))

            // --- Final Note ---
            Text(
                text = "Tip: Provide clear, accurate descriptions for all tickets to avoid confusion. Double-check your details before finalizing — most fields cannot be changed once submitted.",
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                textAlign = TextAlign.Center,
                modifier = Modifier.fillMaxWidth()
            )
        }
    }

    // Full-screen viewer
    if (selectedImageIndex >= 0) {
        FullScreenResViewer(
            imagePainter = painterResource(images[selectedImageIndex]),
            onDismiss = { selectedImageIndex = -1 }
        )
    }
}

@Composable
private fun InfoSectionHeader(title: String) {
    Text(
        text = title,
        style = MaterialTheme.typography.titleMedium.copy(
            fontWeight = FontWeight.Bold,
            color = MaterialTheme.colorScheme.primary
        ),
        modifier = Modifier.fillMaxWidth()
    )
}

@Composable
private fun InfoRow(title: String, description: String, example: String?) {
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(4.dp)
    ) {
        Text(
            text = title,
            style = MaterialTheme.typography.titleSmall.copy(fontWeight = FontWeight.SemiBold),
            color = MaterialTheme.colorScheme.onSurface
        )
        Text(
            text = description,
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )
        if (!example.isNullOrBlank()) {
            Text(
                text = example,
                style = MaterialTheme.typography.bodySmall.copy(fontWeight = FontWeight.Medium),
                color = MaterialTheme.colorScheme.primary
            )
        }
    }
}